import { z } from 'zod';

import { CountryCode, findCountryByAlpha3Code } from '@src/common/auth';
import { parsePhoneNumber } from '@src/common/phoneParsing';
import {
  FOREIGN_IDENTITY_TYPE,
  IDENTITY_TYPE,
  isCnpjForeignConflict,
  isValidBrazilianDocument,
  isValidZipCode,
  meetsPasswordStrength,
  passwordsMatch,
} from '@src/domains/customers';
import { requiredEmailFieldSchema } from '@src/features/customers/auth/core/requiredEmailFieldSchema';
import { isForeignBuyerSelection } from '@src/features/customers/auth/signup/core/resolveSignupFields';
import type { Translate } from '@src/i18n/useTranslate';

export type { SignupFormValues } from '@src/features/customers/auth/signup/types/signupFormValues';

export type SignupSchemaOptions = {
  /** `appInfo.allowForeignBuyers`: gates the "sou estrangeiro" branch entirely. */
  allowForeignBuyers: boolean;
  /** `appInfo.recaptchaKey`: only then is a token expected. */
  requiresRecaptcha: boolean;
};

export const signupSchema = (t: Translate['i18n'], options: SignupSchemaOptions) => {
  const { allowForeignBuyers, requiresRecaptcha } = options;

  const requiredMessage = t('checkout_auth_required_field');
  const optionalString = z.string().optional();

  return z
    .object({
      email: requiredEmailFieldSchema(t),
      identityType: z.enum([IDENTITY_TYPE.CPF, IDENTITY_TYPE.CNPJ], { required_error: requiredMessage }),
      firstName: z.string().min(1, requiredMessage),
      lastName: z.string().min(1, requiredMessage),
      corporateName: optionalString,
      iAmForeign: z.boolean(),
      countryCode: optionalString,
      foreignIdentityType: z
        .enum([FOREIGN_IDENTITY_TYPE.IDENTITY, FOREIGN_IDENTITY_TYPE.PASSPORT])
        .nullable()
        .optional(),
      identity: z.string().min(1, requiredMessage),
      phone: z.string().min(1, requiredMessage),
      zipCode: optionalString,
      password: z.string().min(1, requiredMessage),
      passwordConfirmation: z.string().min(1, requiredMessage),
      agreesToTerms: z.boolean(),
      recaptchaToken: z.string().nullable(),
    })
    .superRefine((values, ctx) => {
      const isForeign = isForeignBuyerSelection(values, allowForeignBuyers);
      const countryCode = isForeign ? values.countryCode : CountryCode.BRAZIL;

      const addIssue = (path: (string | number)[], message: string) =>
        ctx.addIssue({ code: z.ZodIssueCode.custom, path, message });

      const requireField = (path: (string | number)[], value: string | null | undefined) => {
        if (!value?.trim()) addIssue(path, requiredMessage);
      };

      if (isCnpjForeignConflict(values.identityType, values.iAmForeign)) {
        addIssue(['iAmForeign'], t('checkout_auth_signup_foreign_cnpj_conflict'));
      }

      if (values.identityType === IDENTITY_TYPE.CNPJ) {
        requireField(['corporateName'], values.corporateName);
      }

      if (isForeign) {
        requireField(['countryCode'], values.countryCode);
        if (!values.foreignIdentityType) addIssue(['foreignIdentityType'], requiredMessage);
      } else if (!isValidBrazilianDocument(values.identity, values.identityType)) {
        addIssue(
          ['identity'],
          values.identityType === IDENTITY_TYPE.CNPJ
            ? t('checkout_auth_signup_invalid_cnpj')
            : t('checkout_auth_signup_invalid_cpf'),
        );
      }

      const phoneValue = values.phone.trim();
      if (!phoneValue) {
        addIssue(['phone'], requiredMessage);
      } else {
        const phoneCountryAlpha2 = isForeign ? findCountryByAlpha3Code(countryCode ?? '')?.alpha2 : 'BR';
        if (!parsePhoneNumber(phoneValue, phoneCountryAlpha2).isValid) {
          addIssue(['phone'], t('checkout_auth_signup_invalid_phone'));
        }
      }

      // Brazilian PF signup always collects CEP; PJ and foreign buyers keep postal code optional.
      if (!isForeign && values.identityType === IDENTITY_TYPE.CPF) {
        requireField(['zipCode'], values.zipCode);
        if (!isValidZipCode(values.zipCode, countryCode)) {
          addIssue(['zipCode'], t('checkout_auth_signup_invalid_zip_code'));
        }
      } else if (isForeign && values.zipCode?.trim() && !isValidZipCode(values.zipCode, countryCode)) {
        addIssue(['zipCode'], t('checkout_auth_signup_invalid_zip_code'));
      }

      if (!meetsPasswordStrength(values.password)) {
        addIssue(['password'], t('checkout_auth_signup_weak_password'));
      }

      if (!passwordsMatch(values.password, values.passwordConfirmation)) {
        addIssue(['passwordConfirmation'], t('checkout_auth_signup_password_mismatch'));
      }

      if (!values.agreesToTerms) {
        addIssue(['agreesToTerms'], t('checkout_auth_signup_terms_required'));
      }

      if (requiresRecaptcha && !values.recaptchaToken) {
        addIssue(['recaptchaToken'], t('checkout_auth_signup_recaptcha_required'));
      }
    });
};
